import {Store} from "@ngrx/store";
import {Observable} from "rxjs";
import {distinctUntilChanged, map} from "rxjs/operators";

import {CardCommentContextFactory, ContextDimension, buildScopedContext} from "../../shared/comments/card-context-menu/card-comment-context";
import {selectAssetCommentContextIds} from "../store/asset/asset.selectors";

/**
 * Scope name → the id dimensions (beyond the `asset_id` anchor) that identify an asset card's data,
 * and therefore key its comment thread (Comment-Context-Scoping redesign, spec §3). The three cards
 * that show a fund's view of the asset scope deeper; the remaining asset cards stay anchor-only and
 * resolve via the page prefix instead.
 */
export const ASSET_CARD_SCOPE_DIMENSIONS = new Map<string, ContextDimension[]>([
    ["asset", []],
    ["asset-fund-investment", ["fund_id", "fund_investment_id"]],
    ["asset-fund-valuation-forecast", ["fund_id", "fund_valuation_id", "fund_investment_id", "asset_valuation_id"]],
]);

/**
 * Asset-page {@link CardCommentContextFactory}: resolves each card's context from its declared scope
 * and the asset page's live ids ({@link selectAssetCommentContextIds}). When the asset is viewed
 * outside a fund investment, a required id is missing and the scope yields `null`, so the card shows
 * no comment menu — the correct outcome for cards that only make sense inside a fund investment.
 */
export const createAssetCardCommentContextFactory = (store: Store): CardCommentContextFactory => {
    const ids$ = store.select(selectAssetCommentContextIds);
    return {
        build: (scope: string, slug: string): Observable<string | null> => ids$.pipe(
            map(({assetId, fundId, fundValuationId, fundInvestmentId, assetValuationId}) => {
                const available = new Map<ContextDimension, number | null | undefined>([
                    ["fund_id", fundId],
                    ["fund_valuation_id", fundValuationId],
                    ["fund_investment_id", fundInvestmentId],
                    ["asset_valuation_id", assetValuationId],
                ]);
                const dims = new Map<ContextDimension, number | null | undefined>();
                for (const dimension of ASSET_CARD_SCOPE_DIMENSIONS.get(scope) ?? []) {
                    dims.set(dimension, available.get(dimension));
                }
                return buildScopedContext("asset_id", assetId, dims, slug);
            }),
            distinctUntilChanged(),
        ),
    };
};
